import { addDays, isSameDay } from 'date-fns';
import { isSameMonth } from 'date-fns/isSameMonth';
import { isThisMonth } from 'date-fns/isThisMonth';
import { startOfToday } from 'date-fns/startOfToday';
import { toDate } from 'date-fns/toDate';

import getFeatureDecision from '../../../config';
import { DATE_SELECTION_VERSION } from '../../../config/constants';
import customSet from '../../../utils/dateHandling/customSet';

import gridEntryReader from './data-parsing/gridEntryReader';

import type {
  MonthDataGridElement,
  CurrentMonthsInfo,
  OutboundMonthChangeResult,
  HotelValidDatesOnMonthChange,
  DateIndices,
} from '../selectors/types';

// Helper Functions

const areLegsBeforeToday = (
  inboundIndex: number,
  outboundIndex: number,
  currentMonthsInfo: CurrentMonthsInfo,
  isReturn: boolean,
) => {
  if (currentMonthsInfo.outboundThisMonth) {
    const todayIndex = startOfToday().getDate() - 1;
    if (outboundIndex < todayIndex) return true;

    if (isReturn && currentMonthsInfo.inboundThisMonth) {
      if (inboundIndex < todayIndex) return true;
    }
  }
  return false;
};

const addExtraDayForSameDayReturn = (
  changedInboundDate: Date,
  currentOutboundDate: Date,
) => {
  if (isSameDay(changedInboundDate, currentOutboundDate)) {
    return addDays(toDate(changedInboundDate), 1);
  }

  return changedInboundDate;
};

export const gridEntryPassesFilters = (
  gridEntry: MonthDataGridElement | undefined,
  inboundIndex: number,
  outboundIndex: number,
  currentMonthsInfo: CurrentMonthsInfo,
  isReturn: boolean,
  preferDirects: boolean,
) => {
  if (!gridEntry) return false;

  // Filter on inbound after outbound
  if (currentMonthsInfo.sameMonth && inboundIndex < outboundIndex) {
    return false;
  }

  // Filter on current date
  if (
    areLegsBeforeToday(inboundIndex, outboundIndex, currentMonthsInfo, isReturn)
  ) {
    return false;
  }

  // Filter on directness
  const entryIsIndirect = !gridEntryReader.isEntryDirect(gridEntry, isReturn);

  if (preferDirects && entryIsIndirect) {
    return false;
  }
  return true;
};

const getFirstValidDates = (
  grid: MonthDataGridElement[][],
  isReturn: boolean,
  preferDirects: boolean,
  currentMonthsInfo: CurrentMonthsInfo,
) => {
  let currentInboundIndex = null;
  let currentOutboundIndex = null;
  grid.find((inboundRow, inboundIndex) => {
    // Fill with all valid outbound entries to match this inbound date
    let validColumnEntries: MonthDataGridElement[] = [];
    if (currentMonthsInfo.sameMonth) {
      for (let validIndex = 0; validIndex <= inboundIndex; validIndex += 1) {
        validColumnEntries.push(inboundRow[validIndex]);
      }
    } else {
      // No need to keep inbound day before outbound day when inbound month is later than outbound
      // month
      validColumnEntries = inboundRow;
    }
    // Check all outbound/inbound date combinations for this inbound date value of this loop
    // iteration
    return validColumnEntries.find((outboundColumnEntry, outboundIndex) => {
      if (
        gridEntryPassesFilters(
          outboundColumnEntry,
          inboundIndex,
          outboundIndex,
          currentMonthsInfo,
          isReturn,
          preferDirects,
        )
      ) {
        currentInboundIndex = inboundIndex;
        currentOutboundIndex = outboundIndex;
        return true;
      }
      return false;
    });
  });

  if (currentInboundIndex === null && currentOutboundIndex === null) {
    return null;
  }

  const outboundMonthDate = currentMonthsInfo.outbound;
  const outboundDate = customSet(
    outboundMonthDate.getFullYear(),
    outboundMonthDate.getMonth(),
    currentOutboundIndex ? currentOutboundIndex + 1 : 1,
  );

  const dates: { outboundDate: Date; inboundDate?: Date } = { outboundDate };

  if (isReturn && currentMonthsInfo.inbound) {
    const inboundMonthDate = currentMonthsInfo.inbound;
    dates.inboundDate = customSet(
      inboundMonthDate.getFullYear(),
      inboundMonthDate.getMonth(),
      currentInboundIndex ? currentInboundIndex + 1 : 1,
    );
  }
  return dates;
};

const getCurrentMonthsInfo = (
  outboundMonthDate: Date,
  inboundMonthDate: Date | null,
): CurrentMonthsInfo => {
  const currentMonthInfo: CurrentMonthsInfo = {
    outbound: outboundMonthDate,
    outboundThisMonth: isThisMonth(outboundMonthDate),
    sameMonth: false,
  };
  if (inboundMonthDate) {
    currentMonthInfo.inbound = inboundMonthDate;
    currentMonthInfo.inboundThisMonth = isThisMonth(inboundMonthDate);
    currentMonthInfo.sameMonth = isSameMonth(
      outboundMonthDate,
      inboundMonthDate,
    );
  } else {
    currentMonthInfo.sameMonth = false;
  }

  return currentMonthInfo;
};

const findDatesPreservingAnExistingDate = (
  grid: MonthDataGridElement[][],
  firstDates: { outboundDate: Date; inboundDate?: Date },
  dateIndices: DateIndices,
  currentMonthsInfo: CurrentMonthsInfo,
  isReturn: boolean,
  preferDirects: boolean,
) => {
  const {
    currentInboundIndex,
    currentOutboundIndex,
    firstInboundIndex,
    firstOutboundIndex,
    sanitizedOutboundIndex,
  } = dateIndices;

  const inboundMonthDate = currentMonthsInfo.inbound;
  const outboundMonthDate = currentMonthsInfo.outbound;

  if (
    gridEntryPassesFilters(
      grid[firstInboundIndex][currentOutboundIndex],
      firstInboundIndex,
      currentOutboundIndex,
      currentMonthsInfo,
      isReturn,
      preferDirects,
    )
  ) {
    // Try to preserve outbound date
    const dates: { outboundDate: Date; inboundDate?: Date } = {
      outboundDate: customSet(
        outboundMonthDate.getFullYear(),
        outboundMonthDate.getMonth(),
        currentOutboundIndex + 1,
      ),
    };
    if (isReturn) {
      dates.inboundDate = firstDates.inboundDate;
    }
    return dates;
  }
  if (
    gridEntryPassesFilters(
      grid[currentInboundIndex][firstOutboundIndex],
      currentInboundIndex,
      firstOutboundIndex,
      currentMonthsInfo,
      isReturn,
      preferDirects,
    )
  ) {
    // Try to preserve inboundDate
    const dates: { outboundDate: Date; inboundDate?: Date } = {
      outboundDate: firstDates.outboundDate,
    };
    if (isReturn && inboundMonthDate) {
      dates.inboundDate = customSet(
        inboundMonthDate.getFullYear(),
        inboundMonthDate.getMonth(),
        currentInboundIndex + 1,
      );
    }
    return dates;
  }
  if (
    gridEntryPassesFilters(
      grid[sanitizedOutboundIndex][currentOutboundIndex],
      sanitizedOutboundIndex,
      currentOutboundIndex,
      currentMonthsInfo,
      isReturn,
      preferDirects,
    )
  ) {
    // Try and preserve outbound date by bumping invalid inbound up to match outbound date instead
    // of using first possible inbound
    const dates: { outboundDate: Date; inboundDate?: Date } = {
      outboundDate: customSet(
        outboundMonthDate.getFullYear(),
        outboundMonthDate.getMonth(),
        currentOutboundIndex + 1,
      ),
    };
    if (isReturn) {
      dates.inboundDate = dates.outboundDate;
    }
    return dates;
  }

  return null;
};

const getLeastChangedValidDates = (
  grid: MonthDataGridElement[][],
  currentInboundIndex: number,
  currentOutboundIndex: number,
  isReturn: boolean,
  preferDirects: boolean,
  currentMonthsInfo: CurrentMonthsInfo,
) => {
  const firstDates = getFirstValidDates(
    grid,
    isReturn,
    preferDirects,
    currentMonthsInfo,
  );

  if (!firstDates) {
    return null;
  }

  let firstInboundIndex = 0;
  if (firstDates.inboundDate) {
    firstInboundIndex = firstDates.inboundDate.getDate() - 1;
  }

  const dateIndices = {
    firstInboundIndex,
    firstOutboundIndex: firstDates.outboundDate.getDate() - 1,
    currentInboundIndex,
    currentOutboundIndex,
    sanitizedOutboundIndex: isReturn ? currentOutboundIndex : 0,
  };

  const preservedDates = findDatesPreservingAnExistingDate(
    grid,
    firstDates,
    dateIndices,
    currentMonthsInfo,
    isReturn,
    preferDirects,
  );

  return preservedDates || firstDates;
};

const getValidDates = (
  outboundDate: Date,
  outboundMonthDate: Date,
  inboundDate: Date | null,
  inboundMonthDate: Date | null,
  gridData: MonthDataGridElement[][],
  preferDirects: boolean,
  isReturn: boolean,
) => {
  const isValidDate = (date: Date | null) =>
    date instanceof Date && !Number.isNaN(date.getTime());

  const validatedDates: {
    selectedOutbound: Date;
    selectedInbound?: Date;
    changed: boolean;
  } = {
    selectedOutbound: outboundDate,
    changed: false,
  };

  if (isReturn) {
    validatedDates.selectedInbound =
      isValidDate(inboundDate) && inboundDate !== null
        ? inboundDate
        : undefined;
  }

  const inboundIndex =
    isReturn && isValidDate(inboundDate) && inboundDate !== null
      ? inboundDate.getDate() - 1
      : 0;
  const outboundIndex = outboundDate.getDate() - 1;
  const sanitizedInboundIndex = isReturn ? inboundIndex : 0;

  if (isReturn && (inboundIndex < 0 || inboundIndex >= gridData.length)) {
    return validatedDates;
  }

  const gridEntry = gridData[inboundIndex][outboundIndex];
  const currentMonthsInfo = getCurrentMonthsInfo(
    outboundMonthDate,
    isReturn ? (inboundMonthDate ?? outboundMonthDate) : null,
  );
  if (
    gridEntryPassesFilters(
      gridEntry,
      inboundIndex,
      outboundIndex,
      currentMonthsInfo,
      isReturn,
      preferDirects,
    )
  ) {
    return validatedDates;
  }

  const inboundInPast =
    inboundIndex < outboundIndex && currentMonthsInfo.sameMonth;
  if (
    inboundInPast &&
    gridEntryPassesFilters(
      gridData[sanitizedInboundIndex][outboundIndex],
      sanitizedInboundIndex,
      outboundIndex,
      currentMonthsInfo,
      isReturn,
      preferDirects,
    )
  ) {
    validatedDates.changed = true;
    validatedDates.selectedOutbound = outboundDate;
    if (isReturn) {
      validatedDates.selectedInbound = outboundDate;
    }
    return validatedDates;
  }

  const newDates = getLeastChangedValidDates(
    gridData,
    inboundIndex,
    outboundIndex,
    isReturn,
    preferDirects,
    currentMonthsInfo,
  );

  if (newDates) {
    validatedDates.changed = true;
    validatedDates.selectedOutbound = newDates.outboundDate;
    if (isReturn) {
      validatedDates.selectedInbound = newDates.inboundDate;
    }
  }

  return validatedDates;
};

const getValidHotelDates = (
  checkInDate: Date,
  checkOutDate: Date | null,
  checkInMonth: Date,
  checkOutMonth: Date | null,
) => {
  const validatedDates = {
    selectedCheckIn: checkInDate,
    selectedCheckOut: checkOutDate,
    changed: false,
  };

  const today = startOfToday();

  // dates before today
  if (checkInDate < today) {
    validatedDates.changed = true;
    validatedDates.selectedCheckIn = today;
  }

  // checkout before checkin
  if (checkOutDate && checkOutDate < checkInDate) {
    validatedDates.changed = true;
    validatedDates.selectedCheckIn =
      checkInMonth > today ? checkInMonth : today;
    validatedDates.selectedCheckOut =
      checkOutMonth && checkOutMonth > today ? checkOutMonth : today;
  }

  return validatedDates;
};

const getValidDatesOnOutboundMonthChange = (
  changedOutboundDate: Date,
  currentInboundDate: Date | null,
): OutboundMonthChangeResult => {
  const validated: OutboundMonthChangeResult = {
    outboundMonthDate: changedOutboundDate,
    selectedOutbound: changedOutboundDate,
    minInboundDate: changedOutboundDate,
  };
  if (
    changedOutboundDate &&
    currentInboundDate &&
    changedOutboundDate > currentInboundDate
  ) {
    validated.inboundMonthDate = toDate(changedOutboundDate);
    validated.selectedInbound = toDate(changedOutboundDate);
  }
  return validated;
};

const getHotelValidDatesOnOutboundMonthChange = (
  changedCheckInDate: Date,
  currentCheckOutDate: Date | null,
): HotelValidDatesOnMonthChange => {
  const validated: HotelValidDatesOnMonthChange = {
    checkInMonthDate: changedCheckInDate,
    selectedCheckIn: changedCheckInDate,
    minCheckOutDate: changedCheckInDate,
  };

  if (
    currentCheckOutDate &&
    changedCheckInDate &&
    changedCheckInDate > currentCheckOutDate
  ) {
    validated.checkOutMonthDate = toDate(changedCheckInDate);
    validated.selectedCheckOut = toDate(changedCheckInDate);
  }
  return validated;
};

// Working around strange js Date timezone voodoo
export const isBeforeDay = (dateA: Date, dateB: Date) => {
  if (dateA.getFullYear() < dateB.getFullYear()) return true;
  if (dateA.getFullYear() === dateB.getFullYear()) {
    if (dateA.getMonth() < dateB.getMonth()) return true;
    if (dateA.getMonth() === dateB.getMonth()) {
      if (dateA.getDate() < dateB.getDate()) return true;
    }
    return false;
  }
  return false;
};

const getHotelValidDatesOnInboundMonthChange = (
  currentCheckInDate: Date,
  changedCheckOutDate: Date,
): HotelValidDatesOnMonthChange => {
  const dateSelectionVersionExperiment = getFeatureDecision(
    DATE_SELECTION_VERSION,
    false,
  );

  const validated: HotelValidDatesOnMonthChange = {
    checkOutMonthDate: changedCheckOutDate,
    selectedCheckOut: changedCheckOutDate,
    minCheckOutDate: changedCheckOutDate,
  };

  if (
    changedCheckOutDate &&
    currentCheckInDate &&
    changedCheckOutDate < currentCheckInDate
  ) {
    validated.checkInMonthDate = toDate(changedCheckOutDate);
    validated.selectedCheckIn = toDate(changedCheckOutDate);
  }

  if (dateSelectionVersionExperiment) {
    const checkout = addExtraDayForSameDayReturn(
      changedCheckOutDate,
      currentCheckInDate,
    );
    validated.checkOutMonthDate = checkout;
    validated.selectedCheckOut = checkout;
  }

  return validated;
};

const getValidDatesOnInboundMonthChange = (
  currentOutboundDate: Date,
  changedInboundDate: Date,
) => {
  const dateSelectionVersionExperiment = getFeatureDecision(
    DATE_SELECTION_VERSION,
    false,
  );

  const validated: {
    inboundMonthDate: Date;
    selectedInbound: Date;
    minInboundDate: Date;
    outboundMonthDate?: Date;
    selectedOutbound?: Date;
  } = {
    inboundMonthDate: changedInboundDate,
    selectedInbound: changedInboundDate,
    minInboundDate: changedInboundDate,
  };

  if (changedInboundDate < currentOutboundDate) {
    validated.outboundMonthDate = toDate(changedInboundDate);
    validated.selectedOutbound = toDate(changedInboundDate);
  }

  if (dateSelectionVersionExperiment) {
    const checkout = addExtraDayForSameDayReturn(
      changedInboundDate,
      currentOutboundDate,
    );
    validated.inboundMonthDate = checkout;
    validated.selectedInbound = checkout;
  }

  return validated;
};

export default {
  getValidDatesOnOutboundMonthChange,
  getHotelValidDatesOnInboundMonthChange,
  getHotelValidDatesOnOutboundMonthChange,
  getValidDatesOnInboundMonthChange,
  getValidDates,
  getValidHotelDates,
  gridEntryPassesFilters,
};
